import { motion } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, Calendar, Star } from "lucide-react";
import { SiGithub } from "react-icons/si";
import { useSpotlight } from "../hooks/useSpotlight";
import { usePhoneScene, useStoryMode } from "../hooks/useStoryMode";
import { DUR, EASE, STAGGER, VIEWPORT } from "../lib/motion";
import TextReveal from "../components/TextReveal/TextReveal";
import WorkScene from "../components/Morph/scenes/WorkScene";
import { PROJECTS, Project } from "../data/projects";
import "./Project.css";

function ProjectCard({ project, idx }: { project: Project; idx: number }) {
    const ref = useRef<HTMLDivElement>(null);
    // Spotlight, not tilt: these cards are tall and text-heavy, and rotating
    // them under the cursor made the very paragraphs someone is trying to read
    // slide away from them.
    useSpotlight(ref);

    return (
        <motion.div
            ref={ref}
            className="project-card"
            // Same tip-into-place as the bento cards (see About.tsx for the
            // reasoning), at a smaller angle: these are far wider, and the
            // outer edges of a wide slab travel much further for the same
            // number of degrees.
            initial={{ opacity: 0, y: 34, rotateX: 5, scale: 0.985 }}
            whileInView={{ opacity: 1, y: 0, rotateX: 0, scale: 1 }}
            viewport={VIEWPORT}
            transition={{ duration: DUR.slow, delay: idx * STAGGER * 2, ease: EASE }}
        >
            <span className="card-beat" aria-hidden="true" />
            <div className="project-card-inner">
                <div className="project-meta">
                    <span className="project-period mono">
                        <Calendar size={12} strokeWidth={2} />
                        {project.period}
                    </span>
                    {project.featured && (
                        <span className="project-featured mono">
                            <Star size={12} strokeWidth={2} fill="currentColor" />
                            Featured
                        </span>
                    )}
                </div>

                <div className="project-head">
                    <h3 className="project-title">{project.title}</h3>
                    <p className="project-role mono">{project.role}</p>
                </div>

                <p className="project-description">{project.description}</p>

                <ul className="project-highlights">
                    {project.highlights.map((h, i) => (
                        <li key={i}>
                            <span className="bullet" aria-hidden="true" />
                            <span>{h}</span>
                        </li>
                    ))}
                </ul>

                {/* Tags pop in one by one, like nodes of the system coming online —
                    the phone's take on the desktop diagram. */}
                <motion.div
                    className="project-tech"
                    initial="hidden"
                    whileInView="shown"
                    viewport={VIEWPORT}
                    variants={{ hidden: {}, shown: { transition: { staggerChildren: 0.07, delayChildren: 0.2 } } }}
                >
                    {project.tech.map(({ name, icon: Icon }) => (
                        <motion.span
                            key={name}
                            className="tech-tag"
                            title={name}
                            variants={{
                                hidden: { opacity: 0, scale: 0.6, y: 8 },
                                shown: { opacity: 1, scale: 1, y: 0, transition: { type: "spring", stiffness: 420, damping: 22 } },
                            }}
                        >
                            <Icon className="tech-icon" />
                            <span className="tech-name">{name}</span>
                        </motion.span>
                    ))}
                </motion.div>

                <div className="project-actions">
                    {project.repos.map((r) => (
                        <a
                            key={r.label}
                            href={r.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="project-action"
                        >
                            <SiGithub size={14} />
                            <span>{r.label}</span>
                            <ArrowUpRight size={12} strokeWidth={2} className="action-arrow" />
                        </a>
                    ))}
                </div>
            </div>
        </motion.div>
    );
}

export default function Projects() {
    // Story mode plays each project as the "Schematic Unfold" scene.
    const story = useStoryMode();
    const phone = usePhoneScene();
    if (story || phone) return <WorkScene phone={!story} />;

    return (
        <div className="projects-page">
            <div className="projects-container">
                <div className="projects-header">
                    <TextReveal as="h1" className="projects-title" mutedFrom={1}>
                        Featured Projects
                    </TextReveal>
                    <TextReveal as="p" className="projects-subtitle" delay={0.14}>
                        {"End-to-end systems I built — from RESTful API design and EF Core optimization to React/Next.js frontends and Azure deployment."}
                    </TextReveal>
                </div>

                <div className="projects-list">
                    {PROJECTS.map((project, idx) => (
                        <ProjectCard key={project.title} project={project} idx={idx} />
                    ))}
                </div>
            </div>
        </div>
    );
}
